<template>
  <div class="addresses-page">
    <div class="addresses-header">
      <h1>My Addresses</h1>
      <button class="add-address-btn" @click="showAddAddressModal = true">
        + ADD NEW ADDRESS
      </button>
    </div>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <div v-if="loading" class="loading">
      Loading addresses...
    </div>

    <div v-else-if="addresses.length === 0" class="no-addresses">
      <p>You have no saved addresses.</p>
    </div>

    <div v-else class="addresses-list">
      <div v-for="address in addresses" :key="address.id" class="address-card">
        <div class="address-header">
          <h3>{{ address.name }}</h3>
          <div class="address-actions">
            <button @click="editAddress(address)" class="edit-btn">Edit</button>
            <button @click="deleteAddress(address.id)" class="delete-btn">Delete</button>
          </div>
        </div>
        
        <div class="address-details">
          <p>{{ address.street_address }}</p>
          <p v-if="address.apt_suite">{{ address.apt_suite }}</p>
          <p>{{ address.city }}, {{ address.state }} {{ address.postal_code }}</p>
          <p>{{ address.country }}</p>
        </div>
        
        <div v-if="address.is_default" class="default-badge">
          Default Address
        </div>
        <button 
          v-else 
          @click="setAsDefault(address.id)"
          class="set-default-btn"
        >
          Set as Default
        </button>
      </div>
    </div>

    <!-- Add/Edit Address Modal -->
    <div v-if="showAddAddressModal || editingAddress" class="modal" @click.self="handleModalClick">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ editingAddress ? 'Edit Address' : 'Add New Address' }}</h2>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>

        <form @submit.prevent="saveAddress" class="address-form">
          <div class="form-group">
            <label for="name">Name</label>
            <input 
              type="text" 
              id="name" 
              v-model="addressForm.name" 
              required
            >
          </div>

          <div class="form-group">
            <label for="street">Street Address</label>
            <input 
              type="text" 
              id="street" 
              v-model="addressForm.streetAddress" 
              required
            >
          </div>

          <div class="form-group">
            <label for="apt">Apartment/Suite (Optional)</label>
            <input 
              type="text" 
              id="apt" 
              v-model="addressForm.aptSuite"
            >
          </div>

         
            <div class="form-group">
              <label for="city">City</label>
              <input 
                type="text" 
                id="city" 
                v-model="addressForm.city" 
                required
              >
            </div>

            <div class="form-group">
              <label for="state">State/Prov.</label>
              <input 
                type="text" 
                id="state" 
                v-model="addressForm.state" 
                required
              >
            </div>
          

          <div class="form-row">
            <div class="form-group">
              <label for="country">Country</label>
              <div class="country-select-container">
                <input
                  type="text"
                  id="country"
                  v-model="addressForm.country"
                  @focus="showCountryDropdown"
                  @click="showCountryDropdown"
                  @keydown="handleKeyDown"
                  readonly
                  required
                  class="country-select-input"
                  :placeholder="addressForm.country || 'Select a country...'"
                >
                <div class="country-dropdown" :class="{ show: isCountryDropdownVisible }">
                  <div class="country-search">
                    <input
                      type="text"
                      v-model="countrySearch"
                      placeholder="Search countries..."
                      @input="filterCountries"
                      @keydown.stop="handleSearchKeyDown"
                      @click.stop
                      ref="searchInput"
                    >
                  </div>
                  <div 
                    v-for="(country, index) in filteredCountries" 
                    :key="country"
                    class="country-option"
                    :class="{
                      selected: country === addressForm.country,
                      'keyboard-selected': index === selectedIndex
                    }"
                    @click.stop="selectCountry(country)"
                    @mouseover="selectedIndex = index"
                  >
                    {{ country }}
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label for="postal">Postal Code</label>
              <input 
                type="text" 
                id="postal" 
                v-model="addressForm.postalCode" 
                required
              >
            </div>
          </div>

          <div class="form-group">
            <label for="phone">Phone Number (Optional)</label>
            <input 
              type="text" 
              id="phone" 
              v-model="addressForm.phone"
              placeholder="Enter phone number"
            >
          </div>

          <div class="form-group checkbox">
            <label>
              <input 
                type="checkbox" 
                v-model="addressForm.isDefault"
              >
              Set as default address
            </label>
          </div>

          <button type="submit" class="save-btn">
            {{ editingAddress ? 'Update Address' : 'Save Address' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config';

// Configure axios to include credentials
axios.defaults.withCredentials = true;

const countries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", 
  "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", 
  "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", 
  "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", 
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", 
  "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", 
  "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", 
  "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", 
  "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos", 
  "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", 
  "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", 
  "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", 
  "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", 
  "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", 
  "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", 
  "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", 
  "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", 
  "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", 
  "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", 
  "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", 
  "Yemen", "Zambia", "Zimbabwe"
];

export default {
  name: 'Addresses',
  data() {
    return {
      addresses: [],
      loading: true,
      error: null,
      showAddAddressModal: false,
      editingAddress: null,
      countries,
      countrySearch: '',
      isCountryDropdownVisible: false,
      addressForm: {
        name: '',
        streetAddress: '',
        aptSuite: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        phone: '',
        isDefault: false
      },
      selectedIndex: -1,
    }
  },
  computed: {
    filteredCountries() {
      const search = this.countrySearch.toLowerCase();
      const filtered = this.countries.filter(country => 
        country.toLowerCase().includes(search)
      );
      // If we have filtered results and no selection, select the first one
      if (filtered.length > 0 && this.selectedIndex === -1) {
        this.$nextTick(() => {
          this.selectedIndex = 0;
        });
      }
      return filtered;
    }
  },
  mounted() {
    // Add click event listener to handle clicks outside the dropdown
    document.addEventListener('mousedown', this.handleOutsideClick);
  },
  beforeDestroy() {
    // Remove the event listener when component is destroyed
    document.removeEventListener('mousedown', this.handleOutsideClick);
  },
  methods: {
    async fetchAddresses() {
      try {
        this.loading = true;
        this.error = null;
        
        const response = await axios.get(`${config.apiBaseUrl}/api/addresses?_t=${Date.now()}`, {
          withCredentials: true,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        // Handle the response data
        if (response.data) {
          // Convert object to array if needed
          if (typeof response.data === 'object' && !Array.isArray(response.data)) {
            // Remove _cachedAt property and convert to array
            const { _cachedAt, ...addressesObj } = response.data;
            this.addresses = Object.values(addressesObj);
          } else {
            this.addresses = response.data;
          }
        } else {
          this.addresses = [];
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
        this.error = 'Failed to load addresses. Please try again.';
        this.addresses = [];
      } finally {
        this.loading = false;
      }
    },
    editAddress(address) {
      this.editingAddress = address;
      this.addressForm = {
        name: address.name,
        streetAddress: address.street_address,
        aptSuite: address.apt_suite || '',
        city: address.city,
        state: address.state,
        country: address.country,
        postalCode: address.postal_code,
        phone: address.phone || '',
        isDefault: address.is_default
      };
    },
    async deleteAddress(id) {
      if (!confirm('Are you sure you want to delete this address?')) {
        return;
      }

      try {
        const response = await axios.delete(`${config.apiBaseUrl}/api/addresses/${id}`, {
          withCredentials: true,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (response.status === 200 || response.status === 204) {
          // Remove the address from the local state immediately
          this.addresses = this.addresses.filter(addr => addr.id !== id);
          
          // Force a fresh fetch of addresses with a small delay
          await new Promise(resolve => setTimeout(resolve, 100));
          await this.fetchAddresses();
          
          this.error = null;
          
          // Show success message
          this.$root.$emit('show-toast', {
            message: 'Address deleted successfully',
            type: 'success'
          });
        } else {
          throw new Error('Failed to delete address');
        }
      } catch (error) {
        console.error('Error deleting address:', error);
        
        // Get the error message from the response
        let errorMessage;
        if (error.response?.status === 500) {
          errorMessage = 'Cannot delete this address because it is being used in orders. Please try setting a different address as default instead.';
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = 'Failed to delete address. Please try again.';
        }
        
        this.error = errorMessage;
        
        // Show error toast
        this.$root.$emit('show-toast', {
          message: this.error,
          type: 'error'
        });
      }
    },
    async setAsDefault(id) {
      try {
        const address = this.addresses.find(addr => addr.id === id);
        if (!address) {
          throw new Error('Address not found');
        }
        
        const response = await axios.put(
          `${config.apiBaseUrl}/api/addresses/${id}`, 
          {
            name: address.name,
            streetAddress: address.street_address,
            aptSuite: address.apt_suite,
            city: address.city,
            state: address.state,
            country: address.country,
            postalCode: address.postal_code,
            phone: address.phone || '',
            isDefault: true
          },
          {
            withCredentials: true,
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          }
        );
        
        if (response.status === 200) {
          // Force a fresh fetch of addresses
          await this.fetchAddresses();
          this.error = null;
          
          // Show success message
          this.$root.$emit('show-toast', {
            message: 'Default address updated successfully',
            type: 'success'
          });
        } else {
          throw new Error('Failed to set default address');
        }
      } catch (error) {
        console.error('Error setting default address:', error);
        this.error = 'Failed to set default address. Please try again.';
        
        // Show error toast
        this.$root.$emit('show-toast', {
          message: this.error,
          type: 'error'
        });
      }
    },
    async saveAddress() {
      try {
        const payload = {
          name: this.addressForm.name,
          streetAddress: this.addressForm.streetAddress,
          aptSuite: this.addressForm.aptSuite,
          city: this.addressForm.city,
          state: this.addressForm.state,
          country: this.addressForm.country,
          postalCode: this.addressForm.postalCode,
          phone: this.addressForm.phone,
          isDefault: this.addressForm.isDefault
        };

        let response;
        if (this.editingAddress) {
          response = await axios.put(
            `${config.apiBaseUrl}/api/addresses/${this.editingAddress.id}?_t=${Date.now()}`,
            payload,
            {
              withCredentials: true,
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                'X-Requested-With': 'XMLHttpRequest'
              }
            }
          );
        } else {
          response = await axios.post(
            `${config.apiBaseUrl}/api/addresses?_t=${Date.now()}`,
            payload,
            {
              withCredentials: true,
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                'X-Requested-With': 'XMLHttpRequest'
              }
            }
          );
        }

        // Close the modal first
        this.closeModal();

        // Force a fresh fetch of addresses with a timestamp to bypass cache
        await this.fetchAddresses();
        
        this.error = null;

        // Show success message
        this.$root.$emit('show-toast', {
          message: 'Address saved successfully',
          type: 'success'
        });
      } catch (error) {
        console.error('Error saving address:', error);
        this.error = 'Failed to save address. Please try again.';
        
        // Show error toast
        this.$root.$emit('show-toast', {
          message: this.error,
          type: 'error'
        });
      }
    },
    closeModal() {
      this.showAddAddressModal = false;
      this.editingAddress = null;
      this.addressForm = {
        name: '',
        streetAddress: '',
        aptSuite: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        phone: '',
        isDefault: false
      };
      this.error = null;
    },
    handleModalClick() {
      this.closeModal();
    },
    handleOutsideClick(event) {
      // Check if the click is outside the country-select-container
      if (!event.target.closest('.country-select-container') && this.isCountryDropdownVisible) {
        this.hideCountryDropdown();
      }
    },
    showCountryDropdown(event) {
      event.stopPropagation();
      this.isCountryDropdownVisible = true;
      this.countrySearch = '';
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
          this.$refs.searchInput.select();
          if (this.filteredCountries.length > 0) {
            this.selectedIndex = 0;
          }
        }
      });
    },
    handleSearchKeyDown(event) {
      const totalItems = this.filteredCountries.length;
      
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (totalItems > 0) {
            if (this.selectedIndex >= totalItems - 1) {
              this.selectedIndex = 0;
            } else {
              this.selectedIndex++;
            }
            this.scrollToSelected();
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (totalItems > 0) {
            if (this.selectedIndex <= 0) {
              this.selectedIndex = totalItems - 1;
            } else {
              this.selectedIndex--;
            }
            this.scrollToSelected();
          }
          break;
        case 'Enter':
          event.preventDefault();
          if (this.selectedIndex >= 0 && this.filteredCountries[this.selectedIndex]) {
            this.selectCountry(this.filteredCountries[this.selectedIndex]);
          } else if (totalItems > 0) {
            this.selectCountry(this.filteredCountries[0]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.hideCountryDropdown();
          break;
        case 'Tab':
          this.hideCountryDropdown();
          break;
      }
    },
    handleKeyDown(event) {
      // Main input only handles Escape key
      if (event.key === 'Escape') {
        event.preventDefault();
        this.hideCountryDropdown();
      }
    },
    scrollToSelected() {
      this.$nextTick(() => {
        const selectedElement = document.querySelector('.country-option.keyboard-selected');
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    selectCountry(country) {
      this.addressForm.country = country;
      this.hideCountryDropdown();
      // Focus the next input (postal code) after selection
      this.$nextTick(() => {
        const postalInput = document.getElementById('postal');
        if (postalInput) {
          postalInput.focus();
        }
      });
    },
    hideCountryDropdown() {
      this.isCountryDropdownVisible = false;
      this.countrySearch = '';
      this.selectedIndex = -1;
    },
    filterCountries() {
      // Don't reset selection when filtering
      this.$nextTick(() => {
        const dropdown = document.querySelector('.country-dropdown');
        if (dropdown) {
          dropdown.scrollTop = 0;
        }
      });
    },
  },
  watch: {
    // Reset selectedIndex when search changes
    countrySearch() {
      this.selectedIndex = -1;
    }
  },
  created() {
    this.fetchAddresses();
  }
}
</script>

<style scoped>
.addresses-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.addresses-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.add-address-btn {
  background-color: #000;
  color: white;
  padding: 10px 20px;
  border: none;
  cursor: pointer;
  font-weight: bold;
}

.addresses-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.address-card {
  border: 1px solid #ddd;
  padding: 20px;
  border-radius: 4px;
  position: relative;
}

.address-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.address-actions {
  display: flex;
  gap: 10px;
}

.edit-btn, .delete-btn {
  padding: 5px 10px;
  border: 1px solid #ddd;
  background: none;
  cursor: pointer;
}

.delete-btn {
  color: #ff4444;
}

.default-badge {
  position: absolute;
  right: 10px;
  background-color: #28a745;
  color: white;
  padding: 5px 10px;
  border-radius: 3px;
  font-size: 12px;
}

.set-default-btn {
  margin-top: 10px;
  background: none;
  border: 1px solid #28a745;
  color: #28a745;
  padding: 5px 10px;
  cursor: pointer;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 4px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  z-index: 2001;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: sticky;
  top: 0;
  background: white;
  z-index: 2002;
  border-bottom: 1px solid #eee;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.address-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

label {
  margin-bottom: 5px;
  font-weight: bold;
}

input[type="text"] {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.checkbox {
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.save-btn {
  background-color: #000;
  color: white;
  padding: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.error-message {
  color: #ff4444;
  margin-bottom: 20px;
  padding: 10px;
  background-color: #ffe5e5;
  border-radius: 4px;
}

.loading {
  text-align: center;
  padding: 20px;
  color: #666;
}

.no-addresses {
  text-align: center;
  padding: 40px;
  color: #666;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.country-select-container {
  position: relative;
}

.country-select-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  background: white;
  transition: all 0.3s ease;
}

.country-select-input:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 5px rgba(0, 102, 204, 0.3);
}

.country-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 250px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 2003;
  display: none;
  margin-top: 2px;
}

.country-dropdown.show {
  display: block;
}

.country-search {
  position: sticky;
  top: 0;
  padding: 8px;
  background: white;
  border-bottom: 1px solid #eee;
}

.country-search input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.country-search input:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 5px rgba(0, 102, 204, 0.3);
}

.country-option {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  user-select: none;
}

.country-option:hover {
  background-color: #f0f7ff;
}

.country-option.selected,
.country-option.keyboard-selected {
  background-color: #0066cc;
  color: white;
}
</style> 